import { useState, useMemo } from 'react';
import { Check, Filter } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface ColumnFilterProps {
  title: string;
  options: Array<{ label: string; value: string | number }>;
  selectedValues: Set<string | number>;
  onApply: (values: Set<string | number>) => void;
  searchable?: boolean;
}

export function ColumnFilter({
  title,
  options,
  selectedValues,
  onApply,
  searchable = true,
}: ColumnFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedValues, setTempSelectedValues] = useState<Set<string | number>>(
    new Set(selectedValues)
  );

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Handle checkbox change
  const handleToggle = (value: string | number) => {
    const newSet = new Set(tempSelectedValues);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    setTempSelectedValues(newSet);
  };

  // Handle select all
  const handleSelectAll = () => {
    setTempSelectedValues(new Set(filteredOptions.map(opt => opt.value)));
  };

  // Handle clear all
  const handleClearAll = () => {
    setTempSelectedValues(new Set());
  };

  // Handle apply
  const handleApply = () => {
    onApply(tempSelectedValues);
    setIsOpen(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setTempSelectedValues(new Set(selectedValues));
    setSearchQuery('');
    setIsOpen(false);
  };

  // Reset temp values when opening
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setTempSelectedValues(new Set(selectedValues));
      setSearchQuery('');
    }
    setIsOpen(open);
  };

  const hasActiveFilter = selectedValues.size > 0 && selectedValues.size < options.length;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-6 px-1 ml-2",
            hasActiveFilter && "text-primary"
          )}
        >
          <Filter className={cn("h-3 w-3", hasActiveFilter && "fill-primary")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="flex flex-col">
          {/* Header */}
          <div className="px-3 py-2 border-b">
            <h4 className="font-medium text-sm">{title}</h4>
          </div>

          {/* Search */}
          {searchable && (
            <div className="px-3 py-2 border-b">
              <SearchInput
                placeholder="Search..."
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>
          )}

          {/* Select All / Clear All */}
          <div className="flex gap-2 px-3 py-2 border-b">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              className="h-7 flex-1 text-xs"
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              className="h-7 flex-1 text-xs"
            >
              Clear All
            </Button>
          </div>

          {/* Options */}
          <ScrollArea className="h-[240px]">
            <div className="px-3 py-2 space-y-2">
              {filteredOptions.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 rounded px-2 py-1"
                    onClick={() => handleToggle(option.value)}
                  >
                    <Checkbox
                      checked={tempSelectedValues.has(option.value)}
                      onCheckedChange={() => handleToggle(option.value)}
                    />
                    <label className="text-sm flex-1 cursor-pointer">
                      {option.label}
                    </label>
                    {tempSelectedValues.has(option.value) && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Footer Actions */}
          <div className="flex gap-2 px-3 py-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              className="flex-1"
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
